// ═══════════════════════════════════════════════════════════════════════
// Paramètres › Dispositifs sociaux › une règle pour une portée (dispositif entier, une campagne, un type d'aide) :
// historique des versions (version, valeur, période, statut, auteur, valideur), versions en préparation et leur
// circuit (simuler, soumettre, valider par une AUTRE personne, refuser, retirer) ; saisie d'une nouvelle version
// typée selon le gabarit, avec sa date d'effet (jamais rétroactive : le serveur refuse), sa source (statuts,
// assemblée générale, bureau), la référence de la pièce et le motif. Routes : /api/configuration/versions…
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Plus, Calculator, Send, Ban, CheckCircle2, XCircle, ShieldAlert } from 'lucide-react';
import { Carte, Bouton, BandeauInfo, Badge, Squelette, formaterDate } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import { lireCfg, ecrireCfg, CONFIANCES, BadgeStatutRegle, FenetreValider, FenetreMotifCfg } from './configurationCommun';
import {
  SOURCES_DISPOSITIF, ChampRegle, ErreurCfg, ImpactVersion, aujourdHui, erreurCfg, reglablePour, toArray, valeurEnvoyee, valeurInitiale, valeurRemplie,
} from './dispositifsCommun';

/** Corps commun d'une version : valeur typée, source, pièce, confiance, motif. */
function corpsVersion(regle, f) {
  return {
    code: regle.code, valeur: valeurEnvoyee(regle, f.valeur), source: f.source, reference_juridique: f.reference_juridique.trim() || null,
    niveau_confiance: f.niveau_confiance, motif: f.motif.trim(), niveau: 'SOGAS',
  };
}

/**
 * Préparer (ou modifier) une version d'une règle de dispositif. Hors règlement : portée et date d'effet saisies ;
 * dans un règlement (pack) : la règle prend la portée et la date du règlement.
 */
export function FenetreVersionDispositif({ regle, portee, version, pack, onFermer, onTermine }) {
  const [f, setF] = useState(() => ({
    valeur: valeurInitiale(regle, version ? version.valeur_saisie : undefined),
    date_debut: version ? version.date_debut : '', date_fin: version ? version.date_fin || '' : '',
    source: version ? version.source : (pack ? pack.source_nature : 'DECISION_BUREAU'),
    reference_juridique: version ? version.reference_juridique || '' : (pack ? pack.source_reference || '' : ''),
    niveau_confiance: version ? version.niveau_confiance : 'CONFIRME_SOGAS', motif: version ? version.motif || '' : '',
  }));
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const enregistrer = async () => {
    setEnCours(true); setErreur(null);
    try {
      const corps = corpsVersion(regle, f);
      if (version) {
        await ecrireCfg('patch', `/versions/${version.id}`, pack ? corps : { ...corps, date_debut: f.date_debut, date_fin: f.date_fin || null });
      } else if (pack) {
        await ecrireCfg('post', '/versions', { ...corps, pack_id: pack.id });
      } else {
        await ecrireCfg('post', '/versions', { ...corps, date_debut: f.date_debut, date_fin: f.date_fin || null,
          portee_dispositif_id: portee.dispositifId, portee_objet_type: portee.objetType || null, portee_objet_id: portee.objetId || null });
      }
      onTermine(pack ? 'Règle enregistrée dans le règlement.' : 'Brouillon enregistré. Simulez-le puis soumettez-le à validation.');
    } catch (e) { setErreur(erreurCfg(e)); setEnCours(false); }
  };
  const pret = valeurRemplie(regle, f.valeur) && f.motif.trim().length >= 3 && (pack || f.date_debut);
  return (
    <Fenetre titre={`${version ? 'Modifier' : 'Nouvelle version'} — ${regle.libelle}`} Icone={Plus} onFermer={onFermer} large={regle.type === 'MATRICE' || regle.type === 'TEXTE'}>
      <ErreurCfg erreur={erreur} />
      <p style={{ margin: '0 0 12px', fontSize: 13 }}>
        Portée : <strong>{portee.libelle}</strong>
        {pack && <> · règlement « {pack.libelle} », à partir du {formaterDate(pack.date_debut)}</>}
      </p>
      <ChampRegle r={regle} valeur={f.valeur} onChange={(v) => setF({ ...f, valeur: v })} id="valeur-regle" />
      {!pack && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <Champ libelle="Date d'effet" requis aide="Aujourd'hui ou plus tard : une règle de dispositif ne s'applique jamais au passé.">
            <input type="date" id="date-effet" className="rh-champ-saisie" min={aujourdHui()} value={f.date_debut} onChange={maj('date_debut')} />
          </Champ>
          <Champ libelle="Date de fin" aide="Facultative : une version suivante la ferme d'elle-même.">
            <input type="date" className="rh-champ-saisie" value={f.date_fin} onChange={maj('date_fin')} />
          </Champ>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Champ libelle="Source de la décision" requis>
          <select id="source-version" className="rh-champ-saisie" value={f.source} onChange={maj('source')}>
            {Object.entries(SOURCES_DISPOSITIF).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Niveau de confiance" requis>
          <select className="rh-champ-saisie" value={f.niveau_confiance} onChange={maj('niveau_confiance')}>
            {Object.entries(CONFIANCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
      </div>
      <Champ libelle="Pièce ou référence de la décision" aide="Exemple : procès-verbal du bureau, résolution d'assemblée générale (date, numéro).">
        <input id="reference-version" className="rh-champ-saisie" value={f.reference_juridique} onChange={maj('reference_juridique')} />
      </Champ>
      <Champ libelle="Motif" requis><textarea id="motif-version" className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={Plus} disabled={!pret || enCours} onClick={enregistrer}>
          {version ? 'Enregistrer' : pack ? 'Ajouter au règlement' : 'Préparer le brouillon'}
        </Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

const memePortee = (v, portee) => v.portee_dispositif_id === portee.dispositifId && (v.portee_objet_id || '') === (portee.objetId || '');
const porteeVersion = (v, portee) => (!v.portee_objet_id ? 'Dispositif entier' : v.portee_objet_id === portee.objetId ? 'Cette portée' : 'Autre');

function CarteVersionEnCours({ v, droits, moi, onAgir, onSous }) {
  const estAuteur = !!moi && v.auteur_id === moi;
  return (
    <Carte titre={`Version ${v.version ?? ''} : ${v.valeur} · à partir du ${formaterDate(v.date_debut)}`} action={<BadgeStatutRegle statut={v.statut} />}>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
        {SOURCES_DISPOSITIF[v.source] || v.source}{v.reference_juridique ? ` (${v.reference_juridique})` : ''} · préparée par {v.auteur || '—'} · motif : {v.motif}
        {v.motif_refus_ou_annulation && v.statut.code === 'BROUILLON' ? ` · renvoyée : ${v.motif_refus_ou_annulation}` : ''}
      </p>
      {v.pack_id ? <p style={{ margin: 0, fontSize: 13 }}>Fait partie d'un règlement : elle se soumet et se valide avec lui (section « Règlements »).</p> : (
        <>
          <ImpactVersion simulation={v.simulation} />
          {v.statut.code === 'A_VALIDER' && estAuteur && (
            <BandeauInfo ton="alerte" titre="Double regard">Vous avez préparé cette version : sa validation revient à une autre personne habilitée.</BandeauInfo>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {v.statut.code === 'BROUILLON' && droits.preparer && <>
              <Bouton petit onClick={() => onSous({ type: 'modifier', version: v })}>Modifier</Bouton>
              <Bouton petit Icone={Calculator} onClick={() => onAgir(() => ecrireCfg('post', `/versions/${v.id}/simuler`), 'Simulation calculée (aucune donnée modifiée).')}>Simuler</Bouton>
              <Bouton petit variante="primaire" Icone={Send} onClick={() => onAgir(() => ecrireCfg('post', `/versions/${v.id}/soumettre`), 'Soumise à validation. Une autre personne habilitée doit la valider.')}>Soumettre à validation</Bouton>
            </>}
            {v.statut.code === 'A_VALIDER' && droits.simuler && <Bouton petit Icone={Calculator} onClick={() => onAgir(() => ecrireCfg('post', `/versions/${v.id}/simuler`), 'Simulation recalculée.')}>Relancer la simulation</Bouton>}
            {v.statut.code === 'A_VALIDER' && (v.peut_valider || (droits.valider && !estAuteur)) && <Bouton petit variante="primaire" Icone={CheckCircle2} onClick={() => onSous({ type: 'valider', version: v })}>Valider</Bouton>}
            {v.statut.code === 'A_VALIDER' && droits.valider && <Bouton petit variante="danger" Icone={XCircle} onClick={() => onSous({ type: 'refuser', version: v })}>Refuser</Bouton>}
            {(droits.preparer || droits.valider) && <Bouton petit variante="discret" Icone={Ban} onClick={() => onSous({ type: 'annuler', version: v })}>Retirer</Bouton>}
          </div>
        </>
      )}
    </Carte>
  );
}

/** Fiche d'une règle pour la portée choisie : définition, versions, circuit hors règlement. */
export function FenetreRegleDispositif({ regle, portee, moi, onFermer, onChange }) {
  const { donnees, erreur, recharger } = useChargement(() => lireCfg(`/regles/${regle.code}`), [regle.code]);
  const [sous, setSous] = useState(null);
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState(null);
  const apres = async (m) => { setSous(null); setErreurAction(null); setMessage(m); await recharger(); onChange(); };
  const agir = async (fn, m) => {
    try { setMessage(''); setErreurAction(null); await fn(); await apres(m); } catch (e) { setErreurAction(erreurCfg(e)); }
  };
  if (!donnees) {
    return <Fenetre titre={regle.libelle} onFermer={onFermer} large>{erreur ? <BandeauInfo ton="danger">{erreur}</BandeauInfo> : <Squelette hauteur={120} />}</Fenetre>;
  }
  const droits = donnees.droits || {};
  const versions = toArray(donnees.versions).filter((v) => v.portee_dispositif_id === portee.dispositifId && (!v.portee_objet_id || v.portee_objet_id === portee.objetId));
  const enCours = versions.filter((v) => memePortee(v, portee) && ['BROUILLON', 'A_VALIDER'].includes(v.statut.code));
  const historique = versions.filter((v) => !['BROUILLON', 'A_VALIDER'].includes(v.statut.code));
  const reglable = reglablePour(regle, portee.objetType);
  return (
    <Fenetre titre={regle.libelle} Icone={ShieldAlert} onFermer={onFermer} large>
      {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
      <ErreurCfg erreur={erreurAction} />
      <p style={{ margin: '0 0 8px', fontSize: 14 }}>
        Portée : <strong>{portee.libelle}</strong>{regle.obligatoire && <> · <Badge ton="info">Obligatoire</Badge></>}
      </p>
      {regle.aide && <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--sg-texte-2)' }}>{regle.aide}</p>}
      {!reglable && <BandeauInfo ton="info">Cette règle se règle pour le dispositif entier : choisissez « dispositif entier » comme portée pour en préparer une version.</BandeauInfo>}
      {reglable && droits.preparer && <Bouton variante="primaire" Icone={Plus} onClick={() => setSous({ type: 'preparer' })}>Préparer une nouvelle version</Bouton>}
      {enCours.length > 0 && <h3 style={{ fontSize: 15, margin: '16px 0 8px' }}>En préparation et à valider</h3>}
      {enCours.map((v) => <CarteVersionEnCours key={v.id} v={v} droits={droits} moi={moi} onAgir={agir} onSous={setSous} />)}
      <h3 style={{ fontSize: 15, margin: '16px 0 8px' }}>Versions</h3>
      {historique.length === 0 ? <p style={{ fontSize: 14 }}>Aucune version validée pour cette portée.</p> : (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Version</th><th>Valeur</th><th>Période</th><th>Portée</th><th>Statut</th><th>Source</th><th>Préparée par</th><th>Validée par</th></tr></thead>
            <tbody>
              {historique.map((v) => (
                <tr key={v.id}>
                  <td>{v.version ?? '—'}</td>
                  <td>{v.valeur}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formaterDate(v.date_debut)}{v.date_fin ? ` → ${formaterDate(v.date_fin)}` : ' → …'}</td>
                  <td>{porteeVersion(v, portee)}</td>
                  <td><BadgeStatutRegle statut={v.statut} /></td>
                  <td>{SOURCES_DISPOSITIF[v.source] || v.source}</td>
                  <td>{v.auteur || '—'}</td>
                  <td>{v.valideur || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(sous?.type === 'preparer' || sous?.type === 'modifier') && (
        <FenetreVersionDispositif regle={regle} portee={portee} version={sous.version} onFermer={() => setSous(null)} onTermine={apres} />
      )}
      {sous?.type === 'valider' && (
        <FenetreValider titre={`Valider : ${regle.libelle} = ${sous.version.valeur}`} resume={<ImpactVersion simulation={sous.version.simulation} />}
          onFermer={() => setSous(null)} onValider={async (commentaire) => {
            await ecrireCfg('post', `/versions/${sous.version.id}/valider`, { commentaire, confirmation: true });
            await apres('Version validée. Elle s\'applique automatiquement à sa date d\'effet.');
          }} />
      )}
      {sous?.type === 'refuser' && (
        <FenetreMotifCfg titre="Refuser" aide="La version revient en brouillon à son auteur, avec votre motif." onFermer={() => setSous(null)}
          onConfirmer={async (motif) => { await ecrireCfg('post', `/versions/${sous.version.id}/refuser`, { motif }); await apres('Version renvoyée en brouillon.'); }} />
      )}
      {sous?.type === 'annuler' && (
        <FenetreMotifCfg titre="Retirer cette version" aide="La version est annulée ; elle reste visible dans l'historique." libelleBouton="Retirer" onFermer={() => setSous(null)}
          onConfirmer={async (motif) => { await ecrireCfg('post', `/versions/${sous.version.id}/annuler`, { motif }); await apres('Version retirée.'); }} />
      )}
    </Fenetre>
  );
}
